package ui.resource

import androidx.compose.material3.SnackbarDuration
import androidx.compose.runtime.*
import api.ResourceType
import common.frontend.api.*
import io.Repository
import io.ktor.http.*
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import srializable.JSON_INSTANCE
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.*
import ui.resource.ResourceListingScope.PickerType
import ui.snackBarStateGlobal
import kotlin.time.Duration.Companion.minutes

@VM
class ResourceListingVM : NavigationViewModel() {

    private lateinit var scopeUi: ResourceListingScope

    var selected by mutableStateOf(setOf<Io.ListingResource.Item>())
        private set

    var state by mutableDataState<ResourceListingScope.Wrapper>()
        private set

    var uploadState by mutableDataState<FilePickerUploadState>()
        private set

    lateinit var selectedOrientation: MutableState<ResourceListingScope.PickerOrientation>
        private set

    sealed interface Action {
        class OnItemSelected(val item: ResourceListingScope.Wrapper.Item) : Action
        data class AddLocalMedia(val pickerType: PickerType) : Action
        companion object {

            @Composable
            fun rememberAction(vm: ResourceListingVM): (Action) -> Unit {
//                TODO AFTER PICKER OK, remove
                val nav = runCatching {
                    LocalNavigation.current
                }.getOrNull()

                return remember {
                    { act ->
                        when (act) {
                            is AddLocalMedia -> vm.addLocalMedia(act.pickerType.type, act.pickerType.customVideoFormat)
                            is OnItemSelected -> vm.onItemSelected(act.item, nav)
                        }
                    }
                }
            }
        }
    }

    fun init(scopeUi: ResourceListingScope) {
        this.scopeUi = scopeUi
        if (state !is DataState.Result) {
            state = DataState.Loading()
        }

        selectedOrientation = mutableStateOf(scopeUi.resOrientations.first())
        selected = setOf()
        scope.launch {
            runCatching {

                val wrapper = ResourceListingScope.Wrapper(
                    canAdd = scopeUi.login.canResourceAdd(scopeUi.args.companyId),
                    canEdit = scopeUi.login.canResourceEdit(scopeUi.args.companyId),
                    canCustomVideoType = (scopeUi.login.isServerAdmin() && scopeUi.login.server.id == 18_000L) || scopeUi.login.isSuperAdmin(),
                    selectedType = mutableStateOf(scopeUi.resTypes.first()),
                    listing = Repository.api.resource.listing(scopeUi.args.companyId, scopeUi.args.restrictionType)
                )

                state = DataState.Result(wrapper)

                if (!wrapper.canAddNewResource()) {
                    snackBarStateGlobal.showSnackbar(
                        message = Res.string.media_listing__storage_full.await(),
                        withDismissAction = true
                    )
                }
            }.onFailure {
                it.printStackTraceInDebug()
                state = DataState.Error(it.message!!, throwable = it)
            }
        }
    }

    private fun onItemSelected(item: ResourceListingScope.Wrapper.Item, nav: Navigation?) {
        if (scopeUi.args.pickerMode) {
            GlobalObjectShare.onImagePick(setOf(item.data))
            nav?.up()
        } else {
//                TODO AFTER PICKER OK, remove
            if (!scopeUi.multiselect) scopeUi.onPick?.invoke(setOf(item.data))
            else {
                if (selected.contains(item.data)) {
                    selected -= item.data
                } else {
                    selected += item.data
                }
            }
        }
    }

    private fun addLocalMedia(type: ResourceType, customVideoFormat: Boolean = false) {
        val mimeType = when (type) {
            ResourceType.UNDEFINED,
            ResourceType.ICON,
            ResourceType.RAW,
            ResourceType.TEXT -> return

            ResourceType.IMAGE -> FileFormat.imageMime
            ResourceType.VIDEO -> FileFormat.videoMime
            ResourceType.VIDEO_ALPHA -> FileFormat.videoAlphaMime
            ResourceType.DOCUMENT -> FileFormat.docMime
            ResourceType.WWW_APP -> FileFormat.extra.filter { it.isZip() }.map { it.mime }
            ResourceType.AUDIO -> FileFormat.audioMime
        }

        scope.launch(Dispatchers.Unconfined) {
            state.entry?.apply {
                if (!canAddNewResource()) {
                    snackBarStateGlobal.showSnackbar(
                        message = Res.string.media_listing__storage_full.await(),
                        withDismissAction = true
                    )
                } else {
                    uploadState = DataState.Idle()

                    FilePicker.uploadFromPicker(
                        contentTypes = mimeType,
                        pickerTimeout = 3.minutes,
                        params = object : FilePickerUploadParams {

                            override fun buildUrl(): String = Repository.api.resource.buildFileUploadUrl()

                            override fun buildHeaders(
                                name: String,
                                mime: String
                            ): Array<Pair<String, String>> = buildList {
                                addAll(
                                    listOf(
                                        "Authorization" to Repository.api.authHash(),
                                        "company-id" to scopeUi.args.companyId.toString(),
                                        "file-type" to type.key.lowercase(),
                                        "file-suffix" to FileFormat.fromMime(mime).suffix.encodeURLPathPart(),
                                        "file-name" to name.encodeURLPathPart(),
                                        "file-mime" to mime.encodeURLPathPart(),
                                    )
                                )
                                if (customVideoFormat) {
                                    add("custom-video-format" to "true")
                                }
                            }.toTypedArray()
                        },
                        onNewFile = { json ->
                            runCatching {
                                JSON_INSTANCE.decodeFromString<Io.ListingResource.Item>(json)
                            }.onFailure {
                                it.printStackTrace()
                            }.onSuccess {
                                add(0, it)
                            }
                        }
                    ).collect {
                        if (it.all > 0) {
                            uploadState = if (it.all == (it.failure + it.success)) {
                                DataState.Result(it)
                            } else {
                                DataState.Loading(it)
                            }
                        }
                    }


                    if (uploadState is DataState.Result) {
                        uploadState.entry
                            ?.takeIf { it.all > 0 }
                            ?.apply {
                                when {
                                    failure == 0 -> {
                                        showMsg(
                                            duration = SnackbarDuration.Short,
                                            msg = Res.string.media_listing__upload_toast_ok.await()
                                        )
                                    }

                                    tooLargeFiles != 0 -> {
                                        if (success != 0) {
                                            showMsg(
                                                duration = SnackbarDuration.Indefinite,
                                                msg = Res.string.media_listing__upload_toast_err
                                                    .await(success, failure)
                                            )
                                        }
                                        showMsg(
                                            duration = SnackbarDuration.Indefinite,
                                            msg = Res.string.media_listing__upload_toast_err_large
                                                .await(tooLargeFiles)
                                        )
                                    }

                                    invalidCustomVideoFormat != 0 -> {
                                        if (success != 0) {
                                            showMsg(
                                                duration = SnackbarDuration.Indefinite,
                                                msg = Res.string.media_listing__upload_toast_err
                                                    .await(success, failure)
                                            )
                                        }
                                        showMsg(
                                            duration = SnackbarDuration.Indefinite,
                                            msg = Res.string.media_listing__upload_toast_err_video_format
                                                .await(invalidCustomVideoFormat)
                                        )
                                    }

                                    else -> {
                                        showMsg(
                                            duration = SnackbarDuration.Long,
                                            msg = Res.string.media_listing__upload_toast_err
                                                .await(success, failure)
                                        )
                                    }
                                }
                            }
                        uploadState = DataState.Idle()
                    }
                }
            }
        }
    }

    private suspend fun showMsg(duration: SnackbarDuration, msg: String) {
        snackBarStateGlobal.showSnackbar(
            duration = duration,
            withDismissAction = true,
            message = msg,
        )
    }

    override fun onRelease() {
        state.entry?.release()
        state = DataState.Idle()
    }

}
